import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button, EmptyState, Input, Skeleton, Switch, cn } from "@nexus/ui";
import { Trash2 } from "lucide-react";
import {
  adminTeamRecipientsQueryOptions,
  useCreateTeamRecipient,
  useUpdateTeamRecipient,
  useDeleteTeamRecipient,
} from "@/lib/queries";
import type { TeamRecipientRow } from "@/features/admin/marketplace/types/admin-marketplace.types";

const SKELETON_ROWS = 3;

interface AdminTeamRecipientsProps {
  canList: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

export function AdminTeamRecipients({
  canList,
  canCreate,
  canUpdate,
  canDelete,
}: AdminTeamRecipientsProps) {
  const { t } = useTranslation();
  const { data: recipients = [], isLoading, isError } = useQuery({
    ...adminTeamRecipientsQueryOptions(),
    enabled: canList,
  });

  if (!canList) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {t("common.noPermission", "You don't have permission to view this content.")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="max-w-2xl text-sm text-muted-foreground">
        {t("marketplace.admin.teamRecipients.description")}
      </p>

      {canCreate && <AddRecipientForm />}

      {isLoading ? (
        <RecipientsSkeleton />
      ) : isError ? (
        <div className="py-12 text-center">
          <p className="font-medium">{t("marketplace.admin.teamRecipients.errorTitle")}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("errors.tryAgain", "Please try again.")}
          </p>
        </div>
      ) : recipients.length === 0 ? (
        <EmptyState
          title={t("marketplace.admin.teamRecipients.empty")}
          description={t("marketplace.admin.teamRecipients.emptyDescription")}
        />
      ) : (
        <RecipientsTable
          recipients={recipients}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      )}
    </div>
  );
}

function AddRecipientForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const createRecipient = useCreateTeamRecipient();

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    const trimmedLabel = label.trim();
    createRecipient.mutate(
      { email: trimmedEmail, label: trimmedLabel || null },
      {
        onSuccess: () => {
          setEmail("");
          setLabel("");
        },
      },
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4 sm:flex-row sm:items-end"
    >
      <div className="flex flex-1 flex-col gap-1.5">
        <label
          htmlFor="team-recipient-email"
          className="text-xs font-medium text-muted-foreground"
        >
          {t("marketplace.admin.teamRecipients.emailLabel")}
        </label>
        <Input
          id="team-recipient-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("marketplace.admin.teamRecipients.emailPlaceholder")}
        />
      </div>
      <div className="flex flex-1 flex-col gap-1.5">
        <label
          htmlFor="team-recipient-label"
          className="text-xs font-medium text-muted-foreground"
        >
          {t("marketplace.admin.teamRecipients.labelLabel")}
        </label>
        <Input
          id="team-recipient-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t("marketplace.admin.teamRecipients.labelPlaceholder")}
        />
      </div>
      <Button type="submit" disabled={createRecipient.isPending || !email.trim()}>
        {createRecipient.isPending
          ? t("marketplace.admin.teamRecipients.adding")
          : t("marketplace.admin.teamRecipients.add")}
      </Button>
    </form>
  );
}

interface RecipientsTableProps {
  recipients: TeamRecipientRow[];
  canUpdate: boolean;
  canDelete: boolean;
}

function RecipientsTable({ recipients, canUpdate, canDelete }: RecipientsTableProps) {
  const { t } = useTranslation();
  const updateRecipient = useUpdateTeamRecipient();
  const deleteRecipient = useDeleteTeamRecipient();

  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr className="border-b border-border/50">
            <th className="px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
              {t("marketplace.admin.teamRecipients.table.email")}
            </th>
            <th className="hidden px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70 sm:table-cell">
              {t("marketplace.admin.teamRecipients.table.label")}
            </th>
            <th className="px-5 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
              {t("marketplace.admin.teamRecipients.table.copied")}
            </th>
            <th className="px-5 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {recipients.map((recipient) => (
            <tr
              key={recipient.id}
              className="group transition-colors duration-150 ease-out hover:bg-muted/40"
            >
              <td
                className={cn(
                  "px-5 py-3.5 font-medium tracking-tight",
                  recipient.isActive ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {recipient.email}
              </td>
              <td className="hidden px-5 py-3.5 text-xs text-muted-foreground sm:table-cell">
                {recipient.label ?? <span className="text-muted-foreground/50">—</span>}
              </td>
              <td className="px-5 py-3.5">
                <Switch
                  checked={recipient.isActive}
                  disabled={!canUpdate || updateRecipient.isPending}
                  aria-label={t("marketplace.admin.teamRecipients.toggleAria", {
                    email: recipient.email,
                  })}
                  onCheckedChange={(isActive) =>
                    updateRecipient.mutate({
                      recipientId: recipient.id,
                      payload: { isActive },
                    })
                  }
                />
              </td>
              <td className="px-5 py-3.5">
                <div className="flex items-center justify-end">
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      disabled={deleteRecipient.isPending}
                      onClick={() => deleteRecipient.mutate(recipient.id)}
                      title={t("marketplace.admin.teamRecipients.remove")}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecipientsSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-border/50">
          {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <tr key={i}>
              <td className="px-5 py-3.5">
                <Skeleton className="h-3.5 w-48" />
              </td>
              <td className="hidden px-5 py-3.5 sm:table-cell">
                <Skeleton className="h-3 w-24" />
              </td>
              <td className="px-5 py-3.5">
                <Skeleton className="h-5 w-9 rounded-full" />
              </td>
              <td className="px-5 py-3.5">
                <Skeleton className="ml-auto h-8 w-8" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
